/**
 * GP-03 order view: customer(s) walk in, each with a ticket listing item × count. Counts tick
 * down on every served set; a finished line gets a check mark. Two- and three-customer levels
 * split the area (three on a phone: the portraits peek over their tickets, ticketLayout.ts). A
 * hidden line (hiddenLine) shows a card back and its count until it flips. A goal level carries a
 * counter on the ticket: items left (clearGoal) or golden items left (collectGoal), on the top edge
 * (the bottom edge for three cards on a phone, where the portraits own the top).
 */
import * as Phaser from 'phaser';
import type { CompiledLevel } from '@mp/core';
import { app } from '../game/context.js';
import type { PlayLayout, Rect } from '../game/layout.js';
import { C, FONT } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { deco, dur, reducedMotion } from '../fx/motion.js';
import { t } from '../i18n/index.js';
import { CARD_BACK, bakeCardBack } from './cardBack.js';
import { starPoints } from './goldFrame.js';
import { KEY_BADGE, bakeLockArt } from './lock.js';
import { cardGeometry, cardPart, lineCell, lineGrid } from './ticketLayout.js';

interface Line {
  index: number;
  customer: number;
  icon: Phaser.GameObjects.Image;
  count: Phaser.GameObjects.Text;
  badge: Phaser.GameObjects.Graphics;
  check: Phaser.GameObjects.Image | null;
  cell: Phaser.GameObjects.Container;
  remaining: number;
  /** lockKey: the key badge when this line's item is the level's key */
  key: Phaser.GameObjects.Image | null;
  /** hiddenLine: the card back while the line is face down (its item icon is hidden until the flip) */
  back: Phaser.GameObjects.Image | null;
  /** hiddenLine: while the card flips, what waits for its item to be face up (a served tick of this line) */
  flip: (() => void)[] | null;
}

interface Cust {
  sprite: Phaser.GameObjects.Image;
  id: string;
  ticket: Phaser.GameObjects.Container;
  home: { x: number; y: number };
  /** ticket size (device px, the container's local space) */
  tw: number;
  th: number;
  /** the y (ticket space) where a goal HUD straddles the ticket's edge: the top (0), or just under the bottom when the portrait stands over the top (ticketLayout.ts) */
  hudY: number;
}

/**
 * The goal counter on the ticket(s) and the done stamps: clearGoal's items-left counter under a
 * "Closing time" tag, or collectGoal's golden-items counter (a gold star, a check once they are all popped).
 */
interface Closing {
  kind: 'clear' | 'collect';
  /** clearGoal: the "Closing time" tag (collectGoal has none) */
  tag: Phaser.GameObjects.Container | null;
  counter: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Graphics;
  icon: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  /** collectGoal: the check on the star once no golden item is left */
  check: Phaser.GameObjects.Image | null;
  left: number;
  done: boolean;
  stamps: Phaser.GameObjects.Container[];
}

export class OrderView {
  private lines: Line[] = [];
  private custs: Cust[] = [];
  private closing: Closing | null = null;
  /** hiddenLine: card flips played (test hooks) */
  private flips = 0;

  constructor(
    private scene: Phaser.Scene,
    private L: CompiledLevel,
    lay: PlayLayout,
  ) {
    const n = L.nCustomers;
    for (let c = 0; c < n; c++) this.buildCustomer(c, cardPart(lay.order, n, c, lay.wide), lay.wide);
  }

  private buildCustomer(c: number, r: Rect, wide: boolean): void {
    const u = vp.u;
    const art = app().art;
    const cusId = this.L.level.customers[c]?.id ?? 'cus_ny_001';
    const lineIdx: number[] = [];
    for (let l = 0; l < this.L.nLines; l++) if (this.L.lineCustomer[l] === c) lineIdx.push(l);
    // wide screens stack the customer over the ticket; phones put them side by side (three: the portrait over the ticket's corner)
    const g0 = cardGeometry(r, this.L.nCustomers, wide, u);
    const [custSize, sx, sy] = [g0.cust.size, g0.cust.x, g0.cust.y];
    const { x: tx, y: ty, w: tw, h: th } = g0.ticket;
    const f = art.get(`${cusId}_neutral`);
    const sprite = this.scene.add.image(sx, sy, f.texture, f.frame).setDepth(20);
    sprite.setDisplaySize(custSize, custSize * (sprite.height / sprite.width));
    const home = { x: sprite.x, y: sprite.y };

    const ticket = this.scene.add.container(tx, ty).setDepth(30);
    const rad = 14 * u;
    const artTicket = art.has('ui_play_ticket');
    if (artTicket) {
      const tf = art.get('ui_play_ticket');
      ticket.add(this.scene.add.image(tw / 2, th / 2, tf.texture, tf.frame).setDisplaySize(tw, th));
    } else {
      const g = this.scene.add.graphics();
      g.fillStyle(C.ink, 0.25);
      g.fillRoundedRect(0, 5 * u, tw, th, rad);
      g.fillStyle(0xffffff, 1);
      g.lineStyle(3 * u, C.ink, 1);
      g.fillRoundedRect(0, 0, tw, th, rad);
      g.strokeRoundedRect(0, 0, tw, th, rad);
      g.fillStyle(C.tomato, 1);
      g.fillRoundedRect(0, 0, tw, 12 * u, { tl: rad, tr: rad, bl: 0, br: 0 });
      g.fillStyle(C.creamDeep, 1);
      for (let x = 14 * u; x < tw - 8 * u; x += 18 * u) g.fillCircle(x, th, 3.5 * u);
      ticket.add(g);
    }
    const nl = lineIdx.length;
    const head = artTicket ? th * 0.2 : 14 * u;
    // three cards on a phone: a goal level's tag and counter straddle the bottom edge, so the lines (and their count badges) keep clear of it
    const hudRoom = g0.hud === 'bottom' && (this.L.clear || this.L.collect) ? 10 * u : 0;
    const inner = { x: 8 * u, y: head, w: tw - 16 * u, h: th - head - (artTicket ? th * 0.14 : 8 * u) - hudRoom };
    // grid that gives the biggest item icons
    const { cols, icon } = lineGrid(inner, nl);
    const cw = inner.w / cols;
    const ch = inner.h / Math.ceil(nl / cols);
    lineIdx.forEach((l, k) => {
      const cx = inner.x + cw * (k % cols) + cw / 2;
      const cy = inner.y + ch * Math.floor(k / cols) + ch / 2;
      const cell = this.scene.add.container(cx, cy);
      // the icon and its count badge; a small cell moves the badge out to the icon's corner (ticketLayout lineCell)
      const lc = lineCell(icon, { w: cw, h: ch }, u);
      const itf = art.item(this.L.items[this.L.lineItem[l]!]!);
      const img = this.scene.add.image(lc.ix, lc.iy, itf.texture, itf.frame);
      img.setScale(lc.icon / Math.max(img.width, img.height));
      // count badge (bottom-right), ticks down as sets are served
      const { x: bx, y: by, r: br } = lc.badge;
      const badge = this.scene.add.graphics();
      badge.fillStyle(C.ink, 1);
      badge.fillCircle(bx, by + Math.min(2 * u, br * 0.2), br);
      badge.fillStyle(C.tomato, 1);
      badge.fillCircle(bx, by, br);
      badge.lineStyle(Math.min(2.5 * u, br * 0.25), C.ink, 1);
      badge.strokeCircle(bx, by, br);
      badge.setData('box', { x: bx - br, y: by - br, w: br * 2, h: br * 2 });
      const fs = Math.round(br * 1.25);
      const count = this.scene.add
        .text(bx, by, String(this.L.lineCount0[l]), {
          fontFamily: FONT,
          fontSize: `${fs}px`,
          fontStyle: '700',
          color: '#ffffff',
        })
        .setOrigin(0.5, 0.52);
      cell.add([img, badge, count]);
      ticket.add(cell);
      this.lines[l] = { index: l, customer: c, icon: img, count, badge, check: null, cell, remaining: this.L.lineCount0[l]!, key: null, back: null, flip: null };
    });
    this.custs[c] = { sprite, id: cusId, ticket, home, tw, th, hudY: g0.hud === 'bottom' ? th + 3 * u : 0 };
  }

  /** Flow B: customer walks in, ticket drops in. */
  walkIn(): Promise<void> {
    const d = dur(420);
    if (!d) return Promise.resolve();
    const W = this.scene.scale.width;
    return new Promise((resolve) => {
      this.custs.forEach((c, i) => {
        c.sprite.x = -c.sprite.displayWidth;
        this.scene.tweens.add({ targets: c.sprite, x: c.home.x, duration: d, delay: i * 120, ease: 'Back.easeOut' });
        this.scene.tweens.add({ targets: c.sprite, y: c.home.y - 8 * vp.u, duration: d / 4, yoyo: true, repeat: 1, delay: i * 120 });
        const ty = c.ticket.y;
        c.ticket.y = ty - W * 0.3;
        c.ticket.alpha = 0;
        this.scene.tweens.add({ targets: c.ticket, y: ty, alpha: 1, duration: d, delay: 150 + i * 120, ease: 'Back.easeOut' });
      });
      this.scene.time.delayedCall(d + 150 + this.custs.length * 120, () => resolve());
    });
  }

  /** Tutorial / idle attention pulse on the ticket (Flow A step C). */
  pulse(times = 3): void {
    const d = deco(380);
    if (!d) return;
    for (const c of this.custs) this.scene.tweens.add({ targets: c.ticket, scale: 1.06, duration: d, yoyo: true, repeat: times - 1, ease: 'Sine.easeInOut' });
  }

  /** Centre of a ticket line in world coords (items fly here). */
  linePos(l: number): { x: number; y: number } {
    const line = this.lines[l];
    if (!line) return { x: this.scene.scale.width / 2, y: 0 };
    const m = line.cell.getWorldTransformMatrix();
    return { x: m.tx, y: m.ty };
  }

  serve(l: number, remaining: number): void {
    const line = this.lines[l];
    if (!line) return;
    line.remaining = remaining;
    line.count.setText(String(Math.max(0, remaining)));
    const d = dur(200);
    if (d) this.scene.tweens.add({ targets: line.cell, scale: 1.25, duration: d / 2, yoyo: true, ease: 'Quad.easeOut' });
    if (remaining <= 0) this.complete(line);
    const c = this.custs[line.customer];
    if (c) this.pose(c, 'happy', 700);
  }

  /**
   * A rebuilt ticket (a resume, a resize) shows core's counts: lines already served ticked down,
   * finished ones checked, with no animation (the ticket used to start from the level's counts).
   */
  showServed(remaining: ArrayLike<number>): void {
    for (const line of this.lines) {
      const left = line ? remaining[line.index] : undefined;
      if (!line || left === undefined || left === line.remaining) continue;
      line.remaining = left;
      line.count.setText(String(Math.max(0, left)));
      if (left <= 0) this.complete(line, false);
    }
  }

  private complete(line: Line, animate = true): void {
    const f = app().art.get('ui_check');
    const size = line.icon.displayWidth * 0.7;
    const chk = this.scene.add.image(line.icon.x + size * 0.55, line.icon.y + size * 0.5, f.texture, f.frame);
    chk.setDisplaySize(size, size);
    line.cell.add(chk);
    line.check = chk;
    line.count.setVisible(false);
    line.badge.setVisible(false);
    line.icon.setAlpha(0.55);
    const d = animate ? dur(280) : 0;
    if (d) {
      chk.setScale(0);
      this.scene.tweens.add({ targets: chk, scale: size / chk.width, duration: d, ease: 'Back.easeOut' });
    }
  }

  /**
   * lockKey: the key badge (top-left of the item icon) on these lines, off on the others. A line
   * shows it while the locks are shut and its item is the key.
   */
  setKeyLines(lines: readonly number[], animate: boolean): void {
    bakeLockArt(this.scene);
    for (const line of this.lines) {
      if (!line) continue;
      const on = lines.includes(line.index);
      if (on === !!line.key) continue;
      const d = animate ? dur(180) : 0;
      if (on) {
        const size = line.icon.displayWidth * 0.46;
        const k = this.scene.add.image(line.icon.x - line.icon.displayWidth * 0.42, line.icon.y - line.icon.displayHeight * 0.36, KEY_BADGE);
        k.setScale(size / k.width);
        line.cell.add(k);
        line.key = k;
        continue;
      }
      const k = line.key!;
      line.key = null;
      if (!d) k.destroy();
      else this.scene.tweens.add({ targets: k, scale: k.scale * 1.4, alpha: 0, duration: d, onComplete: () => k.destroy() });
    }
  }

  /** Ticket lines wearing the key badge (test hooks). */
  get keysShown(): number {
    return this.lines.filter((l) => l?.key).length;
  }

  /**
   * hiddenLine: these lines are drawn face down: a card back where the item icon goes (the icon is
   * hidden), the count badge on top as on any line. Called once when the ticket is built, from
   * core's `lineShown` (a round resumed after the reveal draws it face up).
   */
  showFaceDown(lines: readonly number[]): void {
    if (!lines.length) return;
    bakeCardBack(this.scene);
    for (const l of lines) {
      const line = this.lines[l];
      if (!line || line.back) continue;
      const back = this.scene.add.image(line.icon.x, line.icon.y, CARD_BACK);
      back.setScale((Math.max(line.icon.displayWidth, line.icon.displayHeight) * 1.02) / back.width);
      line.cell.addAt(back, 1);
      line.icon.setVisible(false);
      line.back = back;
    }
  }

  /**
   * hiddenLine: the face-down card flips to show the item (a turn edge-on and back; a cross-fade
   * under reduced motion; instant with animations off). `done` runs once the item is face up: at
   * once on a line already up, after the flip on one still down or mid-flip. True when this call
   * started the flip.
   */
  reveal(l: number, done?: () => void): boolean {
    const line = this.lines[l];
    if (line?.flip) {
      if (done) line.flip.push(done);
      return false;
    }
    const back = line?.back;
    if (!line || !back) {
      done?.();
      return false;
    }
    line.back = null;
    line.flip = done ? [done] : [];
    this.flips++;
    const icon = line.icon;
    const s = icon.scaleX;
    const finish = () => {
      back.destroy();
      icon.setScale(s).setAlpha(1).setVisible(true);
      const then = line.flip ?? [];
      line.flip = null;
      for (const fn of then) fn();
    };
    const d = dur(320);
    this.scene.tweens.killTweensOf(back);
    back.setAngle(0);
    if (!d) {
      finish();
      return true;
    }
    if (reducedMotion()) {
      icon.setAlpha(0).setVisible(true);
      this.scene.tweens.add({ targets: icon, alpha: 1, duration: d });
      this.scene.tweens.add({ targets: back, alpha: 0, duration: d, onComplete: finish });
      return true;
    }
    // the card turns edge-on while it lifts a little, then the item turns the rest of the way
    const y = back.y;
    this.scene.tweens.add({
      targets: back,
      scaleX: 0,
      y: y - 6 * vp.u,
      duration: d / 2,
      ease: 'Sine.easeIn',
      onComplete: () => {
        back.setVisible(false);
        icon.setScale(0, s).setVisible(true);
        this.scene.tweens.add({ targets: icon, scaleX: s, duration: d / 2, ease: 'Back.easeOut', onComplete: finish });
      },
    });
    return true;
  }

  /** hiddenLine: attention wiggle on the face-down card (after the mechanic intro): decorative only. */
  wiggleFaceDown(): void {
    const d = deco(90);
    if (d) for (const b of this.backs()) this.shakeBack(b, 8, d);
  }

  /** hiddenLine feedback (a magnet held back only by the face-down line): the card rattles. */
  rattleFaceDown(): void {
    const d = dur(70);
    if (d) for (const b of this.backs()) this.shakeBack(b, 12, d);
  }

  private backs(): Phaser.GameObjects.Image[] {
    return this.lines.flatMap((l) => (l?.back ? [l.back] : []));
  }

  private shakeBack(b: Phaser.GameObjects.Image, deg: number, d: number): void {
    this.scene.tweens.killTweensOf(b);
    b.setAngle(0);
    this.scene.tweens.add({ targets: b, angle: { from: -deg, to: deg }, duration: d, yoyo: true, repeat: 2, ease: 'Sine.easeInOut', onComplete: () => b.setAngle(0) });
  }

  /** hiddenLine: line l is drawn face down (test hooks). */
  isFaceDown(l: number): boolean {
    return !!this.lines[l]?.back;
  }

  /** hiddenLine: card flips played on this ticket (test hooks). */
  get reveals(): number {
    return this.flips;
  }

  /**
   * clearGoal: a "Closing time" tag on the (first) ticket's top-left corner and the items-left
   * counter on the (last) ticket's top-right corner, both straddling the edge so they ride along
   * with the ticket's walk-in and pulses (three cards on a phone: the bottom corners, `hudY`).
   * `done` (a resume after the order was done) stamps it.
   */
  showClosing(left: number, done: boolean): void {
    if (this.closing || !this.custs.length) return;
    const u = vp.u;
    const first = this.custs[0]!;
    // the tag: a small mustard label, slightly tilted like a note pinned on the ticket (three cards on a phone: on its bottom edge)
    const tag = this.scene.add.container(10 * u, first.hudY);
    const tagText = this.scene.add
      .text(0, 0, t('mech.clearGoal.title'), { fontFamily: FONT, fontSize: `${Math.round(13 * u)}px`, fontStyle: '700', color: '#4a2c2a' })
      .setOrigin(0, 0.5);
    const th = 22 * u;
    const tw = tagText.width + 16 * u;
    const tg = this.scene.add.graphics();
    tg.fillStyle(C.ink, 1);
    tg.fillRoundedRect(0, -th / 2 + 2.5 * u, tw, th, th / 2);
    tg.fillStyle(C.mustard, 1);
    tg.lineStyle(2.5 * u, C.ink, 1);
    tg.fillRoundedRect(0, -th / 2, tw, th, th / 2);
    tg.strokeRoundedRect(0, -th / 2, tw, th, th / 2);
    tagText.setX(8 * u);
    tag.add([tg, tagText]);
    tag.setAngle(-4);
    first.ticket.add(tag);
    // the counter: a stack-of-items glyph and "N left"
    this.addCounter('clear', tag, left, done);
  }

  /**
   * collectGoal: the golden-items counter (a gold star and "N left") on the (last) ticket's
   * top-right corner (bottom-right for three cards on a phone), like clearGoal's, with no tag. `done` (a resume after the order was done) stamps it.
   */
  showGolden(left: number, done: boolean): void {
    if (this.closing || !this.custs.length) return;
    this.addCounter('collect', null, left, done);
  }

  private addCounter(kind: Closing['kind'], tag: Phaser.GameObjects.Container | null, left: number, done: boolean): void {
    const u = vp.u;
    const last = this.custs[this.custs.length - 1]!;
    const counter = this.scene.add.container(last.tw - 8 * u, last.hudY);
    const bg = this.scene.add.graphics();
    const icon = this.scene.add.graphics();
    const label = this.scene.add
      .text(0, 0, '', { fontFamily: FONT, fontSize: `${Math.round(15 * u)}px`, fontStyle: '700', color: '#4a2c2a' })
      .setOrigin(1, 0.5);
    counter.add([bg, icon, label]);
    last.ticket.add(counter);
    this.closing = { kind, tag, counter, bg, icon, label, check: null, left: -1, done: false, stamps: [] };
    this.setItemsLeft(left, false);
    if (done) this.orderDone(false);
  }

  /**
   * The counter reads `left`: items still on the board (clearGoal) or golden items not yet popped
   * (collectGoal, a check on its star at 0); it bumps when it changes.
   */
  setItemsLeft(left: number, animate: boolean): void {
    const k = this.closing;
    if (!k || k.left === left) return;
    const changed = k.left >= 0;
    k.left = left;
    k.label.setText(t(k.kind === 'collect' ? 'play.collectGoal.left' : 'play.clearGoal.left', { n: left }));
    if (k.kind === 'collect' && left <= 0 && !k.check) this.checkGolden(animate && changed);
    this.drawCounter();
    const d = animate && changed ? dur(180) : 0;
    if (!d) return;
    const base = k.done ? 1.15 : 1;
    this.scene.tweens.killTweensOf(k.counter);
    k.counter.setScale(base);
    this.scene.tweens.add({ targets: k.counter, scale: base * 1.18, duration: d / 2, yoyo: true, ease: 'Quad.easeOut' });
  }

  private drawCounter(): void {
    const k = this.closing!;
    const u = vp.u;
    const h = 26 * u;
    const pad = 9 * u;
    const ic = 16 * u;
    const w = k.label.width + ic + pad * 2 + 5 * u;
    const fill = k.done ? C.tomato : C.cream;
    k.label.setColor(k.done ? '#ffffff' : '#4a2c2a');
    k.bg.clear();
    k.bg.fillStyle(C.ink, 1);
    k.bg.fillRoundedRect(-w, -h / 2 + 3 * u, w, h, h / 2);
    k.bg.fillStyle(fill, 1);
    k.bg.lineStyle(2.5 * u, C.ink, 1);
    k.bg.fillRoundedRect(-w, -h / 2, w, h, h / 2);
    k.bg.strokeRoundedRect(-w, -h / 2, w, h, h / 2);
    k.label.setX(-pad);
    const x0 = -w + pad;
    k.icon.clear();
    k.icon.lineStyle(2 * u, C.ink, 1);
    if (k.kind === 'collect') {
      // a gold star: golden items (the star the golden tiles wear); a check lands on it at 0
      const pts = starPoints(x0 + ic / 2, 0.5 * u, ic * 0.62, ic * 0.3);
      k.icon.fillStyle(C.star, 1);
      k.icon.fillPoints(pts, true);
      k.icon.strokePoints(pts, true);
      k.check?.setPosition(x0 + ic * 0.62, ic * 0.12);
      return;
    }
    // two stacked tiles: items on the counter
    const s = ic * 0.72;
    k.icon.fillStyle(C.kraft, 1);
    k.icon.fillRoundedRect(x0 + ic - s, -ic / 2, s, s, 3 * u);
    k.icon.strokeRoundedRect(x0 + ic - s, -ic / 2, s, s, 3 * u);
    k.icon.fillStyle(C.creamDeep, 1);
    k.icon.fillRoundedRect(x0, ic / 2 - s, s, s, 3 * u);
    k.icon.strokeRoundedRect(x0, ic / 2 - s, s, s, 3 * u);
  }

  /** collectGoal: no golden item is left: a check lands on the counter's star (the star dims, as a done ticket line's item does). */
  private checkGolden(animate: boolean): void {
    const k = this.closing!;
    const f = app().art.get('ui_check');
    const chk = this.scene.add.image(0, 0, f.texture, f.frame);
    const size = 17 * vp.u;
    chk.setDisplaySize(size, size);
    k.counter.add(chk);
    k.check = chk;
    k.icon.setAlpha(0.6);
    const d = animate ? dur(280) : 0;
    if (!d) return;
    const s = chk.scaleX;
    chk.setScale(0);
    this.scene.tweens.add({ targets: chk, scale: s, duration: d, ease: 'Back.easeOut' });
  }

  /**
   * clearGoal / collectGoal: the ticket is done but the round goes on. Every ticket gets a round
   * "done" stamp, the customers cheer, and the goal counter (items left, golden items left) turns
   * tomato, grows and pulses: it is the goal now.
   */
  orderDone(animate: boolean): void {
    const k = this.closing;
    if (!k || k.done) return;
    k.done = true;
    this.drawCounter();
    const u = vp.u;
    const art = app().art;
    for (const c of this.custs) {
      const r = Math.min(c.tw, c.th) * 0.36;
      const stamp = this.scene.add.container(c.tw * 0.5, c.th * 0.52);
      const g = this.scene.add.graphics();
      g.fillStyle(C.white, 0.6);
      g.fillCircle(0, 0, r);
      g.lineStyle(5 * u, C.teal, 1);
      g.strokeCircle(0, 0, r);
      g.lineStyle(2 * u, C.tealDark, 1);
      g.strokeCircle(0, 0, r - 6 * u);
      const f = art.get('ui_check');
      const chk = this.scene.add.image(0, 0, f.texture, f.frame);
      chk.setScale((r * 1.25) / Math.max(chk.width, chk.height));
      stamp.add([g, chk]);
      stamp.setAngle(-12);
      c.ticket.add(stamp);
      // the stamp goes under the tag and the counter
      if (k.tag?.parentContainer === c.ticket) c.ticket.bringToTop(k.tag);
      if (k.counter.parentContainer === c.ticket) c.ticket.bringToTop(k.counter);
      k.stamps.push(stamp);
      const d = animate ? dur(320) : 0;
      if (d) {
        stamp.setScale(2.2).setAlpha(0);
        this.scene.tweens.add({ targets: stamp, scale: 1, alpha: 1, duration: d, ease: 'Back.easeOut' });
      }
      if (animate) this.pose(c, 'happy', 1200);
    }
    const d = animate ? deco(360) : 0;
    this.scene.tweens.killTweensOf(k.counter);
    if (!d) {
      k.counter.setScale(1.15);
      return;
    }
    k.counter.setScale(1);
    this.scene.tweens.add({
      targets: k.counter,
      scale: 1.15,
      duration: d,
      ease: 'Back.easeOut',
      onComplete: () => this.scene.tweens.add({ targets: k.counter, scale: 1.28, duration: d, yoyo: true, repeat: 2, ease: 'Sine.easeInOut' }),
    });
  }

  /** clearGoal / collectGoal: the tag, counter text, stamps and emphasis drawn (test hooks), or null. */
  closingInfo(): { tag: boolean; counter: string; stamps: number; emphasis: boolean } | null {
    const k = this.closing;
    if (!k) return null;
    return { tag: !!k.tag?.visible, counter: k.label.text, stamps: k.stamps.length, emphasis: k.done };
  }

  /** CSS-independent centre of the goal counter (the banner points at it, golden sparkles fly to it), or null. */
  get counterPos(): { x: number; y: number } | null {
    const k = this.closing;
    if (!k) return null;
    const m = k.counter.getWorldTransformMatrix();
    return { x: m.tx, y: m.ty };
  }

  /**
   * Ticket line icons in world px, with their card and their count badge's box (test hooks: every
   * line on screen, none overlapping, the badge leaving the item readable).
   */
  lineBoxes(): { line: number; customer: number; done: boolean; x: number; y: number; w: number; h: number; badge: { x: number; y: number; w: number; h: number } }[] {
    return this.lines.flatMap((l) => {
      if (!l) return [];
      const b = l.icon.getBounds();
      // the badge circle's box in the cell, through the cell's world transform (a pulse may scale it)
      const m = l.cell.getWorldTransformMatrix();
      const bb = l.badge.getData('box') as { x: number; y: number; w: number; h: number };
      const k = m.scaleX;
      const badge = { x: m.tx + bb.x * k, y: m.ty + bb.y * k, w: bb.w * k, h: bb.h * k };
      return [{ line: l.index, customer: l.customer, done: !!l.check, x: b.x, y: b.y, w: b.width, h: b.height, badge }];
    });
  }

  /** Customer cards (ticket papers) drawn: 1..3 (test hooks). */
  get cards(): number {
    return this.custs.length;
  }

  isLineDone(l: number): boolean {
    return (this.lines[l]?.remaining ?? 0) <= 0;
  }

  pose(c: Cust, pose: 'neutral' | 'happy' | 'sad', ms = 0): void {
    const f = app().art.get(`${c.id}_${pose}`);
    const w = c.sprite.displayWidth;
    c.sprite.setTexture(f.texture, f.frame);
    c.sprite.setDisplaySize(w, w * (c.sprite.height / c.sprite.width));
    if (ms) this.scene.time.delayedCall(ms, () => this.pose(c, 'neutral'));
  }

  allHappy(): void {
    for (const c of this.custs) this.pose(c, 'happy');
    const d = deco(300);
    if (d) for (const c of this.custs) this.scene.tweens.add({ targets: c.sprite, y: c.home.y - 16 * vp.u, duration: d, yoyo: true, repeat: 2, ease: 'Sine.easeOut' });
  }

  allSad(): void {
    for (const c of this.custs) this.pose(c, 'sad');
  }

  allNeutral(): void {
    for (const c of this.custs) this.pose(c, 'neutral');
  }
}
